/**
 * usePerLayerWeights
 * For Media Devoted | Created By Ciro Autiero
 *
 * Owns the group-weight steppers for all three layers (paths/landers/offers).
 * Weights are edited MANUALLY: changing one leaves its siblings untouched, so a
 * layer can total ≠100 until the user balances it (the save gate enforces 100).
 * Stored weights are synced in raw — a layer that already has weights is never
 * re-normalized (that would fight a manual edit on the formula round-trip); only
 * an all-zero / unset layer is seeded to an equal split summing to 100.
 */

import { useCallback, useEffect, useState } from 'react';
import { normalizeWeightsToHundred } from '@trafficpulse/shared';
import type { FlowLayer } from '@trafficpulse/shared';

import type { PerLayerWeights } from './auto-balanced-sliders.math';

export interface UsePerLayerWeightsResult {
  values: PerLayerWeights;
  setValue: (layer: FlowLayer, id: string, next: number) => void;
  reset: (next: PerLayerWeights) => void;
}

export function usePerLayerWeights(
  initial: PerLayerWeights,
): UsePerLayerWeightsResult {
  const [values, setValues] = useState<PerLayerWeights>(() =>
    syncPerLayerWeights(initial),
  );

  useEffect(() => {
    setValues(syncPerLayerWeights(initial));
  }, [stableKey(initial)]);

  // Manual editing: set ONLY this group's weight for this layer. Siblings are
  // left untouched (no auto-rebalance) — the user balances each layer to 100%
  // by hand, and the save gate blocks until every layer totals exactly 100.
  const setValue = useCallback(
    (layer: FlowLayer, id: string, next: number) => {
      setValues((current) => {
        if (!(id in (current[layer] ?? {}))) return current;
        return {
          ...current,
          [layer]: { ...current[layer], [id]: clamp(next) },
        };
      });
    },
    [],
  );

  const reset = useCallback((next: PerLayerWeights) => {
    setValues(syncPerLayerWeights(next));
  }, []);

  return { values, setValue, reset };
}

function clamp(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

/**
 * Keep each layer's weights as-is when it already has any weight (so manual,
 * possibly ≠100 totals survive); seed an equal split (summing exactly 100) only
 * for an all-zero / unset layer.
 */
function syncPerLayerWeights(values: PerLayerWeights): PerLayerWeights {
  const syncLayer = (
    layer: Record<string, number> | undefined,
  ): Record<string, number> => {
    const map = layer ?? {};
    const ids = Object.keys(map);
    if (ids.length === 0) return {};
    const total = ids.reduce((sum, id) => sum + Math.max(0, map[id] ?? 0), 0);
    return total > 0 ? map : normalizeWeightsToHundred(map, ids);
  };
  return {
    paths: syncLayer(values.paths),
    landers: syncLayer(values.landers),
    offers: syncLayer(values.offers),
  };
}

function stableKey(values: PerLayerWeights): string {
  return (['paths', 'landers', 'offers'] as FlowLayer[])
    .map((layer) => `${layer}=${layerKey(values[layer] ?? {})}`)
    .join('|');
}

function layerKey(values: Record<string, number>): string {
  return Object.keys(values)
    .sort()
    .map((k) => `${k}:${values[k]}`)
    .join(',');
}
